import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetWellsQuery } from "@api/mApi";
import { useAppDispatch } from "@app/hooks";
import { selectWell } from "@app/slices/wellContextSlice";
import { useLocale } from "@hooks/useLocale";
import { WellMap } from "@components/map/WellMap";
import { QueryState } from "@components/ui/QueryState";
import { useUnits } from "@units/useUnits";
import { STATUS_KEY as STATUS_MSG } from "@features/wellRecord/wellLabels";
import type { WellView, WellStatus } from "@api/types";

function uniq(xs: string[]): string[] {
  return Array.from(new Set(xs.filter((x) => x && x !== "—"))).sort();
}

/**
 * Well Explorer — the proof-of-stack screen. Spatial-first: a real Leaflet map
 * beside a filterable register table. Reads the well register from Oxbo-M
 * (served by MSW in standalone mode, or the live m.dev service). The M contract
 * filters server-side on `status`; the finer field/basin/operator/search
 * filters are applied client-side over the register (which is small and cached).
 * Clicking a well selects it and drops into its record.
 */
export function WellExplorer() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // Register TD is stored in metres; render it in the viewer's display profile.
  const { convertField, fmt } = useUnits();

  const [search, setSearch] = useState("");
  const [field, setField] = useState("");
  const [basin, setBasin] = useState("");
  const [operator, setOperator] = useState("");
  const [status, setStatus] = useState("");

  // The register itself is fetched unfiltered (small, cached); we derive filter
  // option lists from it and apply all filters client-side for instant feedback.
  const { data: allWells = [], isLoading, isError, refetch } = useGetWellsQuery();

  const fields = useMemo(() => uniq(allWells.map((w) => w.field)), [allWells]);
  const basins = useMemo(() => uniq(allWells.map((w) => w.basin)), [allWells]);
  const operators = useMemo(() => uniq(allWells.map((w) => w.operator)), [allWells]);

  const wells = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allWells.filter((w) => {
      if (q && !`${w.commonName} ${w.uwi} ${w.apiNo ?? ""} ${w.field}`.toLowerCase().includes(q))
        return false;
      if (field && w.field !== field) return false;
      if (basin && w.basin !== basin) return false;
      if (operator && w.operator !== operator) return false;
      if (status && w.status !== status) return false;
      return true;
    });
  }, [allWells, search, field, basin, operator, status]);

  const openWell = (w: WellView) => {
    dispatch(selectWell(w.id));
    navigate("/well");
  };

  const clearFilters = () => {
    setSearch("");
    setField("");
    setBasin("");
    setOperator("");
    setStatus("");
  };

  return (
    <div>
      <div className="page-head">
        <h1>{t("explorer.title")}</h1>
        <p className="subtitle">{t("explorer.subtitle")}</p>
      </div>

      {/* Data-lineage banner: hidden unless the admin overlay (.lineage-on) is set. */}
      <div className="lineage-banner" role="note">
        <span aria-hidden="true">●</span>
        {t("topbar.lineage")} — {t("topbar.lineage.adminOnly")}
      </div>

      <div className="filterbar">
        <div className="field" style={{ flex: "1 1 220px" }}>
          <label htmlFor="wx-search">{t("explorer.filter.field")}</label>
          <input
            id="wx-search"
            type="search"
            placeholder={t("explorer.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t("explorer.search")}
          />
        </div>
        <div className="field">
          <label htmlFor="wx-field">{t("explorer.filter.field")}</label>
          <select id="wx-field" value={field} onChange={(e) => setField(e.target.value)}>
            <option value="">{t("explorer.filter.all")}</option>
            {fields.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="wx-basin">{t("explorer.filter.basin")}</label>
          <select id="wx-basin" value={basin} onChange={(e) => setBasin(e.target.value)}>
            <option value="">{t("explorer.filter.all")}</option>
            {basins.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="wx-operator">{t("explorer.filter.operator")}</label>
          <select id="wx-operator" value={operator} onChange={(e) => setOperator(e.target.value)}>
            <option value="">{t("explorer.filter.all")}</option>
            {operators.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="wx-status">{t("explorer.filter.status")}</label>
          <select id="wx-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{t("explorer.filter.all")}</option>
            {(["drilling", "completion", "producing", "suspended"] as WellStatus[]).map((s) => (
              <option key={s} value={s}>
                {t(STATUS_MSG[s])}
              </option>
            ))}
          </select>
        </div>
        <span className="result-count">{t("explorer.count", { n: wells.length })}</span>
      </div>

      <div className="explorer-split">
        <div className="map-card">
          <WellMap wells={wells} onSelect={openWell} ariaLabel={t("explorer.map.placeholder")} />
        </div>

        <div className="card" style={{ padding: 0 }}>
          <div className="table-scroll">
            <QueryState
              isLoading={isLoading}
              isError={isError}
              isEmpty={wells.length === 0}
              emptyMessage={t("explorer.empty")}
              onRetry={refetch}
            >
              <table>
                <thead>
                  <tr>
                    <th>{t("explorer.col.well")}</th>
                    <th>{t("explorer.col.field")}</th>
                    <th>{t("explorer.col.operator")}</th>
                    <th>{t("explorer.col.status")}</th>
                    <th className="num">{t("explorer.col.td")}</th>
                  </tr>
                </thead>
                <tbody>
                  {wells.map((w) => (
                    <tr
                      key={w.id}
                      className="selectable"
                      data-owner="m"
                      onClick={() => openWell(w)}
                    >
                      <td>
                        <span className="well-link">{w.commonName}</span>
                        <div className="mono" style={{ fontSize: 11, color: "var(--dim)" }}>
                          {w.apiNo ?? w.uwi}
                        </div>
                      </td>
                      <td>{w.field}</td>
                      <td>{w.operator}</td>
                      <td>
                        {w.status ? (
                          <span className={`badge status-${w.status}`}>
                            {t(STATUS_MSG[w.status])}
                          </span>
                        ) : (
                          <span className="badge">{w.statusRaw ?? "—"}</span>
                        )}
                      </td>
                      <td className="num tab-nums">
                        {fmt(convertField("daily_report", "md_current", w.totalDepthM, "metric"))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </QueryState>
          </div>
        </div>
      </div>

      <button type="button" className="btn" style={{ marginTop: 12 }} onClick={clearFilters}>
        {t("explorer.clearFilters")}
      </button>
    </div>
  );
}
